import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Button, Card, Input } from '../components/primitives/index.js'
import SEO from '../components/SEO.jsx'
import TurnstileWidget, { turnstileEnabled } from '../components/TurnstileWidget.jsx'
import AddressAutocomplete from '../components/AddressAutocomplete.jsx'
import { useCart } from '../context/CartContext.jsx'
import { useCustomer } from '../context/CustomerContext.jsx'
import { useProducts } from '../context/ProductsContext.jsx'
import { checkoutConfig as staticConfig } from '../config/checkout.js'
import { applySeoOverrides } from '../config/seo.js'
import { fmtMoney } from '../utils/invoiceUtils.js'
import { api } from '../lib/api.js'
import { validateName } from '../../worker/src/names.js'
import { customerApi, getCustomerToken } from '../lib/customerApi.js'
import './CheckoutPage.css'

export default function CheckoutPage() {
  const navigate = useNavigate()
  const { items, lines, subtotal, bulkSavings, discountCode, discountAmount, applyDiscount, removeDiscount, discountError, clearCart, hydrated } = useCart()
  const { checkoutConfig: liveConfig, inventory, preorderMessage, seoSettings } = useProducts()
  const { customer, logout } = useCustomer()
  const activeConfig = liveConfig ?? staticConfig

  // ── Rewards (signed-in customers): points redemption + store credit ──
  const [rewards, setRewards] = useState(null)
  const [redeemPoints, setRedeemPoints] = useState(0)
  const [creditToApply, setCreditToApply] = useState(0)

  const redeemOption = rewards?.redemptions?.find((r) => r.points === redeemPoints) ?? null
  const redeemAmount = redeemOption ? Math.max(0, Math.min(redeemOption.amount, subtotal - discountAmount)) : 0

  // ── Shipping options from config ─────────────────────────────
  const shippingCfg = activeConfig?.shipping ?? staticConfig.shipping
  const discountedSub = subtotal - discountAmount - redeemAmount
  const standardShipping = discountedSub >= (shippingCfg.freeThreshold ?? 100)
    ? 0 : (shippingCfg.standardRate ?? 9.99)
  const expeditedShipping = shippingCfg.expeditedRate ?? 24.99

  const [shippingMethod, setShippingMethod] = useState('standard')
  const selectedShipping = shippingMethod === 'expedited' ? expeditedShipping : standardShipping

  // ── Insurance from config ─────────────────────────────────────
  const insuranceCfg = activeConfig?.insurance ?? staticConfig.insurance ?? {}
  const insuranceAvailable = insuranceCfg.enabled === true
  const [insuranceSelected, setInsuranceSelected] = useState(false)
  const insurancePrice = insuranceAvailable && insuranceSelected ? (insuranceCfg.price ?? 0) : 0

  // ── Preorder shipping preference ──────────────────────────────
  const hasPreorderItems = items.some((item) => inventory?.[item.lineId]?.preorder === true)
  const [preorderShipping, setPreorderShipping] = useState('separate')
  const preorderShippingCfg = activeConfig?.preorderShipping ?? staticConfig.preorderShipping ?? {}
  const preorderSeparateFee = hasPreorderItems && preorderShipping === 'separate'
    ? (preorderShippingCfg.separateFee ?? 0)
    : 0

  const totalBeforeCredit = Math.max(0, discountedSub + selectedShipping + insurancePrice + preorderSeparateFee)
  const maxCredit = Math.min(rewards?.storeCredit ?? 0, totalBeforeCredit)
  const creditApplied = Math.min(creditToApply, maxCredit)
  const selectedTotal = Math.max(0, totalBeforeCredit - creditApplied)

  const [form, setForm] = useState({
    firstName: '', lastName: '', email: '', address: '', apt: '', city: '', state: '', zip: '', country: 'US',
    notes: '', discord: '',
  })
  // The composed name every downstream consumer still expects — invoice,
  // admin, emails and saved addresses all keep receiving a single `name`.
  const fullName = [form.firstName, form.lastName].map((v) => v.trim()).filter(Boolean).join(' ')
  const [errors, setErrors] = useState({})
  const [code, setCode] = useState('')

  // ── Signed-in customers: prefill from profile + saved addresses ──
  const [prefilled, setPrefilled] = useState(false)
  const [savedAddresses, setSavedAddresses] = useState([])
  const [selectedAddressId, setSelectedAddressId] = useState('new')
  const [saveNewAddress, setSaveNewAddress] = useState(false)

  function fillAddress(a) {
    setForm((f) => ({
      ...f,
      address: a?.address ?? '', apt: a?.apt ?? '', city: a?.city ?? '',
      state: a?.state ?? '', zip: a?.zip ?? '', country: a?.country || 'US',
    }))
  }

  /**
   * A suggestion was picked from the autocomplete. Fills the address block and
   * clears any stale validation errors on the fields it just wrote.
   *
   * Apt is only overwritten when the provider actually returned a unit — a
   * customer who typed "Apt 4B" before picking their street keeps it.
   */
  function applySuggestion(sug) {
    setForm((f) => ({
      ...f,
      address: sug.address ?? '',
      apt: sug.apt ? sug.apt : f.apt,
      city: sug.city ?? '',
      state: sug.state ?? '',
      zip: sug.zip ?? '',
      country: sug.country || f.country || 'US',
    }))
    setErrors((e) => ({ ...e, address: undefined, city: undefined, zip: undefined }))
    setSelectedAddressId('new')
  }

  function selectAddress(id) {
    setSelectedAddressId(id)
    setSaveNewAddress(false)
    if (id === 'new') { fillAddress(null); return }
    const a = savedAddresses.find((x) => x.id === id)
    if (a) fillAddress(a)
  }

  useEffect(() => {
    if (!customer) { setRewards(null); return }
    customerApi.getRewards().then(setRewards).catch(() => setRewards(null))
  }, [customer])

  useEffect(() => {
    if (!customer || prefilled) return
    setPrefilled(true)
    // Accounts store one `name` field; split it for the two checkout inputs.
    const parts = String(customer.name ?? '').trim().split(/\s+/).filter(Boolean)
    setForm((f) => ({
      ...f,
      firstName: f.firstName || (parts[0] ?? ''),
      lastName:  f.lastName  || parts.slice(1).join(' '),
      email: f.email || customer.email || '',
      discord: f.discord || customer.discord || '',
    }))
    customerApi.getAddresses().then(({ addresses }) => {
      setSavedAddresses(addresses ?? [])
      const def = (addresses ?? []).find((a) => a.isDefault) ?? (addresses ?? [])[0]
      if (def) { setSelectedAddressId(def.id); fillAddress(def) }
    }).catch(() => {})
  }, [customer, prefilled])

  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState(null)
  const [turnstileToken, setTurnstileToken] = useState(null)

  const cartEmpty = items.length === 0
  useEffect(() => {
    if (hydrated && cartEmpty && !submitting) navigate('/')
  }, [hydrated, cartEmpty, submitting, navigate])

  if (!hydrated) return null
  if (cartEmpty && !submitting) return null

  function set(field, value) {
    setForm((f) => ({ ...f, [field]: value }))
    setErrors((e) => ({ ...e, [field]: undefined }))
  }

  function validate() {
    const e = {}
    // Same rule the API enforces, imported from the same module — so the
    // message under the field is the message the server would have sent.
    const first = validateName(form.firstName, 'First name')
    if (!first.ok) e.firstName = first.error
    const last = validateName(form.lastName, 'Last name')
    if (!last.ok) e.lastName = last.error
    if (!form.email.trim() || !/\S+@\S+\.\S+/.test(form.email)) e.email = 'Valid email required'
    if (!form.address.trim()) e.address = 'Required'
    if (!form.city.trim()) e.city = 'Required'
    if (!form.zip.trim()) e.zip = 'Required'
    return e
  }

  async function applyCode() {
    if (!code.trim()) return
    await applyDiscount(code.trim())
  }

  async function handleSubmit(e) {
    e.preventDefault()
    const errs = validate()
    if (Object.keys(errs).length > 0) { setErrors(errs); return }
    if (turnstileEnabled && !turnstileToken) {
      setSubmitError('Please complete the verification check below.')
      return
    }

    setSubmitting(true)
    setSubmitError(null)

    // The server is the source of truth: it re-resolves every price, the
    // discount, shipping, fees, and the invoice number from the database.
    // We only send what the customer chose.
    const payload = {
      billingInfo: { ...form, name: fullName },
      items: lines.map((l) => ({ productId: l.productId, variantId: l.variantId, qty: l.qty })),
      discountCode: discountCode?.code ?? null,
      shippingMethod,
      insuranceSelected,
      preorderShipping: hasPreorderItems ? preorderShipping : null,
      redeemPoints: redeemPoints > 0 ? redeemPoints : undefined,
      creditToApply: creditApplied > 0 ? creditApplied : undefined,
      turnstileToken,
    }
    try {
      const res = getCustomerToken()
        ? await customerApi.createOrder(payload)
        : await api.createOrder(payload)
      if (customer && selectedAddressId === 'new' && saveNewAddress) {
        customerApi.addAddress({
          name: fullName, address: form.address, apt: form.apt,
          city: form.city, state: form.state, zip: form.zip, country: form.country,
        }).catch(() => {})
      }
      clearCart()
      navigate('/order-confirm', { state: { order: res.order, email: form.email } })
    } catch (err) {
      setSubmitting(false)
      setSubmitError(err?.message ?? 'Failed to save your order. Please try again.')
      // Tokens are single-use — ask Turnstile for a fresh one for the retry.
      if (turnstileEnabled && window.turnstile) {
        try { window.turnstile.reset() } catch { /* ignore */ }
        setTurnstileToken(null)
      }
    }
  }

  return (
    <div className="container checkout">
      <SEO config={applySeoOverrides({ title: 'Checkout', robots: 'noindex, nofollow', titleRaw: true }, seoSettings?.pages?.checkout)} />
      <h1>Checkout</h1>
      <p className="checkout__lede">We&apos;ll generate an invoice — payment via Discord.</p>

      <div className="checkout__account-banner">
        {customer ? (
          <>
            <span>Signed in as <strong>{customer.name || customer.email}</strong> — your order will be saved to your account.</span>
            <button type="button" onClick={() => logout()}>Not you? Sign out</button>
          </>
        ) : (
          <>
            <span>Checking out as a guest — your order confirmation will let you save it to an account.</span>
            <Link to="/account/login?next=/checkout">Already have an account? Sign in</Link>
          </>
        )}
      </div>

      <form className="checkout__grid" onSubmit={handleSubmit}>
        <div className="checkout__form">
          <Card>
            <h3>Contact</h3>
            <div className="checkout__row">
              <Input label="First name" required autoComplete="given-name" value={form.firstName} onChange={(e) => set('firstName', e.target.value)} error={errors.firstName} />
              <Input label="Last name" required autoComplete="family-name" value={form.lastName} onChange={(e) => set('lastName', e.target.value)} error={errors.lastName} />
            </div>
            <Input label="Email" type="email" required value={form.email} onChange={(e) => set('email', e.target.value)} error={errors.email} />
            <Input
              label="Discord username"
              value={form.discord}
              onChange={(e) => set('discord', e.target.value)}
              placeholder="username (no @ needed)"
            />
          </Card>

          <Card>
            <h3>Shipping address</h3>

            {customer && savedAddresses.length > 0 && (
              <div className="checkout__addresses">
                <span className="checkout__addresses-label">Ship to</span>
                <div className="checkout__addresses-chips">
                  {savedAddresses.map((a) => (
                    <button
                      key={a.id}
                      type="button"
                      className={`checkout__address-chip ${selectedAddressId === a.id ? 'is-active' : ''}`}
                      onClick={() => selectAddress(a.id)}
                    >
                      {a.label || a.address}
                      {a.isDefault && <span className="checkout__address-default">DEFAULT</span>}
                    </button>
                  ))}
                  <button
                    type="button"
                    className={`checkout__address-chip ${selectedAddressId === 'new' ? 'is-active' : ''}`}
                    onClick={() => selectAddress('new')}
                  >
                    + New address
                  </button>
                </div>
              </div>
            )}

            <AddressAutocomplete
              label="Street address"
              required
              value={form.address}
              country={form.country || 'US'}
              onChange={(e) => { set('address', e.target.value); setSelectedAddressId('new') }}
              onSelect={applySuggestion}
              error={errors.address}
            />
            <Input
              label="Apt / Unit / Suite"
              placeholder="Optional"
              autoComplete="address-line2"
              value={form.apt}
              onChange={(e) => set('apt', e.target.value)}
            />

            {customer && selectedAddressId === 'new' && (
              <label className="checkout__save-address">
                <input type="checkbox" checked={saveNewAddress} onChange={(e) => setSaveNewAddress(e.target.checked)} />
                Save this address to my account
              </label>
            )}

            <div className="checkout__row">
              <Input label="City" required value={form.city} onChange={(e) => set('city', e.target.value)} error={errors.city} />
              <Input label="State / Province" value={form.state} onChange={(e) => set('state', e.target.value)} />
            </div>
            <div className="checkout__row">
              <Input label="ZIP / Postal" required value={form.zip} onChange={(e) => set('zip', e.target.value)} error={errors.zip} />
              <Input label="Country" value={form.country} onChange={(e) => set('country', e.target.value)} />
            </div>
            <Input
              label="Order notes (optional)"
              as="textarea"
              value={form.notes}
              onChange={(e) => set('notes', e.target.value)}
            />
          </Card>

          <Card>
            <h3>Shipping method</h3>
            {[
              { id: 'standard', label: shippingCfg.standardLabel ?? 'Standard shipping', price: standardShipping },
              { id: 'expedited', label: shippingCfg.expeditedLabel ?? 'Expedited shipping', price: expeditedShipping },
            ].map((opt) => (
              <label key={opt.id} className={`checkout__option-card ${shippingMethod === opt.id ? 'is-active' : ''}`}>
                <input type="radio" name="shipping" checked={shippingMethod === opt.id} onChange={() => setShippingMethod(opt.id)} />
                <span>{opt.label}</span>
                <span className="checkout__option-price">{opt.price === 0 ? 'Free' : fmtMoney(opt.price)}</span>
              </label>
            ))}

            {insuranceAvailable && (
              <label className={`checkout__option-card ${insuranceSelected ? 'is-active' : ''}`}>
                <input type="checkbox" checked={insuranceSelected} onChange={(e) => setInsuranceSelected(e.target.checked)} />
                <span>
                  {insuranceCfg.label ?? 'Order Insurance'}
                  <small>{insuranceCfg.description}</small>
                </span>
                <span className="checkout__option-price">+{fmtMoney(insuranceCfg.price ?? 0)}</span>
              </label>
            )}
          </Card>

          {hasPreorderItems && (
            <Card>
              <h3>Pre-order shipping preference</h3>
              {[
                {
                  id: 'separate',
                  label: `Ship in-stock items first${preorderShippingCfg.separateFee ? ` (+${fmtMoney(preorderShippingCfg.separateFee)})` : ''}`,
                  desc: preorderShippingCfg.separateDescription ?? 'In-stock items ship immediately. Pre-order items ship separately once available.',
                },
                {
                  id: 'together',
                  label: 'Ship everything together',
                  desc: 'All items held until pre-order stock arrives, then shipped as one package. May take longer.',
                },
              ].map((opt) => (
                <label key={opt.id} className={`checkout__option-card checkout__option-card--wide ${preorderShipping === opt.id ? 'is-active' : ''}`}>
                  <input type="radio" name="preorderShipping" checked={preorderShipping === opt.id} onChange={() => setPreorderShipping(opt.id)} />
                  <span>
                    {opt.label}
                    <small>{opt.desc}</small>
                  </span>
                </label>
              ))}
            </Card>
          )}

          {rewards?.enabled && (rewards.balance > 0 || rewards.storeCredit > 0) && (
            <Card>
              <h3>{rewards.pointsName ?? 'Rewards'} — {rewards.balance} available</h3>
              {(() => {
                const tiers = [
                  { points: 0, amount: 0 },
                  ...(rewards.redemptions ?? []).filter((r) => rewards.balance >= r.points).sort((a, b) => a.points - b.points),
                ]
                if (tiers.length < 2) return null
                const tierIndex = Math.max(0, tiers.findIndex((t) => t.points === redeemPoints))
                const active = tiers[tierIndex]
                return (
                  <div className="checkout__slider">
                    <div className="checkout__slider-row">
                      <span>Redeem points</span>
                      <span className={active.points > 0 ? 'is-value' : ''}>
                        {active.points > 0 ? `${fmtMoney(active.amount)} off · ${active.points} pts` : "Don't redeem"}
                      </span>
                    </div>
                    <input
                      type="range" min={0} max={tiers.length - 1} step={1} value={tierIndex}
                      onChange={(e) => setRedeemPoints(tiers[Number(e.target.value)].points)}
                    />
                  </div>
                )
              })()}

              {rewards.storeCredit > 0 && (
                <div className="checkout__slider">
                  <div className="checkout__slider-row">
                    <span>Apply store credit ({fmtMoney(rewards.storeCredit)} available)</span>
                    <span className={creditApplied > 0 ? 'is-value' : ''}>{fmtMoney(creditApplied)}</span>
                  </div>
                  <input
                    type="range" min={0} max={maxCredit} step={1} value={creditApplied}
                    onChange={(e) => setCreditToApply(Number(e.target.value))}
                  />
                </div>
              )}
            </Card>
          )}

          {turnstileEnabled && <TurnstileWidget onToken={setTurnstileToken} />}

          <Button type="submit" variant="primary" size="lg" loading={submitting} className="checkout__submit">
            {submitting ? 'Saving order…' : 'Generate Invoice →'}
          </Button>

          {submitError && <p className="checkout__submit-error">{submitError}</p>}
          <p className="checkout__terms">
            By generating this invoice you agree to our <Link to="/terms">Terms &amp; Conditions</Link> and consent to receive order-related emails.
          </p>
        </div>

        <motion.div className="checkout__summary" initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }}>
          <Card>
            <h3>Order summary</h3>
            <div className="checkout__items">
              {lines.map((item) => (
                <div key={item.lineId} className="checkout__summary-item">
                  <span>
                    {item.name} <em>&times;{item.qty}</em>
                    {item.bulkApplied && <em className="checkout__bulk-tag"> bulk price</em>}
                  </span>
                  <span>{fmtMoney(item.effectivePrice * item.qty)}</span>
                </div>
              ))}
            </div>

            <div className="checkout__code">
              <Input placeholder="Discount code" value={discountCode ? discountCode.code : code} disabled={!!discountCode} onChange={(e) => setCode(e.target.value.toUpperCase())} error={discountError} />
              {discountCode ? (
                <Button type="button" variant="secondary" onClick={() => { removeDiscount(); setCode('') }}>Remove</Button>
              ) : (
                <Button type="button" variant="secondary" onClick={applyCode}>Apply</Button>
              )}
            </div>

            <div className="checkout__totals">
              <div>
                <span>Subtotal</span>
                <span>{fmtMoney(subtotal)}</span>
              </div>
              {bulkSavings > 0 && (
                <div className="checkout__totals-discount">
                  <span>Bulk savings</span>
                  <span>&minus;{fmtMoney(bulkSavings)}</span>
                </div>
              )}
              {discountAmount > 0 && (
                <div className="checkout__totals-discount">
                  <span>Discount ({discountCode?.code})</span>
                  <span>&minus;{fmtMoney(discountAmount)}</span>
                </div>
              )}
              {redeemAmount > 0 && (
                <div className="checkout__totals-discount">
                  <span>{rewards?.pointsName ?? 'Points'} ({redeemPoints} pts)</span>
                  <span>&minus;{fmtMoney(redeemAmount)}</span>
                </div>
              )}
              {creditApplied > 0 && (
                <div className="checkout__totals-discount">
                  <span>Store credit</span>
                  <span>&minus;{fmtMoney(creditApplied)}</span>
                </div>
              )}
              <div>
                <span>Shipping {shippingMethod === 'expedited' ? '(Expedited)' : '(Standard)'}</span>
                <span>{selectedShipping === 0 ? 'Free' : fmtMoney(selectedShipping)}</span>
              </div>
              {insuranceSelected && insuranceAvailable && (
                <div>
                  <span>{insuranceCfg.label ?? 'Order Insurance'}</span>
                  <span>+{fmtMoney(insuranceCfg.price ?? 0)}</span>
                </div>
              )}
              {preorderSeparateFee > 0 && (
                <div>
                  <span>{preorderShippingCfg.separateLabel ?? 'Pre-order second shipment'}</span>
                  <span>+{fmtMoney(preorderSeparateFee)}</span>
                </div>
              )}
              <div className="checkout__totals-final">
                <span>Total</span>
                <span>{fmtMoney(selectedTotal)}</span>
              </div>
            </div>

            <p className="checkout__no-payment">No payment is taken here. After generating your invoice, send your payment and we&apos;ll match and confirm it.</p>

            {hasPreorderItems && (
              <div className="checkout__preorder-notice">
                <strong>Pre-order Notice</strong>
                <p>{preorderMessage}</p>
              </div>
            )}
          </Card>
        </motion.div>
      </form>
    </div>
  )
}
